import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AtSign,
  Building2,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Mail,
  MailX,
  Search,
  ShieldCheck,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/cn';
import { MailingIntestazione } from '../components/email/MailingIntestazione';
import { useNotifications } from '../store/useStore';
import { ConsentBadge } from '../components/email/ConsentBadge';
import { ImportContactsModal } from '../components/email/ImportContactsModal';
import { SchedaContatto } from '../components/email/SchedaContatto';
import { BarraSelezione } from '../components/email/liste/BarraSelezione';
import { NuovoContattoDialog } from '../components/email/liste/NuovoContattoDialog';
import {
  CONTACTS_PAGE_SIZE,
  emailService,
  type ContactDetail,
  type ContactStats,
} from '../lib/email/emailService';
import { listeService } from '../lib/email/listeService';
import {
  EMAIL_CONSENT_BASIS_LABEL,
  EMAIL_CONTACT_STATUS,
  type EmailContactConListe,
  type EmailContactStatus,
  type EmailLista,
} from '../types/email.types';

type FiltroStato = EmailContactStatus | 'tutti';

const FILTRI: { valore: FiltroStato; etichetta: string }[] = [
  { valore: 'tutti', etichetta: 'Tutti' },
  { valore: 'attivo', etichetta: 'Attivi' },
  { valore: 'in_attesa', etichetta: 'In attesa' },
  { valore: 'disiscritto', etichetta: 'Disiscritti' },
  { valore: 'bounce', etichetta: 'Inesistenti' },
  { valore: 'reclamo', etichetta: 'Spam' },
  { valore: 'soppresso', etichetta: 'Bloccati' },
];

/** Gli errori del database non sono Error ma oggetti con un messaggio: vanno letti anche quelli. */
function messaggioErrore(errore: unknown): string {
  if (errore instanceof Error) return errore.message;
  if (errore && typeof errore === 'object' && 'message' in errore) return String(errore.message);
  return 'Errore imprevisto.';
}

export default function EmailContactsPage() {
  const { addNotification } = useNotifications();

  const [contatti, setContatti] = useState<EmailContactConListe[]>([]);
  const [totale, setTotale] = useState(0);
  const [stats, setStats] = useState<ContactStats>({ totale: 0, attivi: 0, inAttesa: 0, fuori: 0 });
  const [liste, setListe] = useState<EmailLista[]>([]);
  const [loading, setLoading] = useState(true);

  const [ricerca, setRicerca] = useState('');
  const [ricercaAttiva, setRicercaAttiva] = useState('');
  const [stato, setStato] = useState<FiltroStato>('tutti');
  const [pagina, setPagina] = useState(0);

  const [selezionati, setSelezionati] = useState<Set<string>>(new Set());
  const [lavorandoSelezione, setLavorandoSelezione] = useState(false);

  const [mostraImport, setMostraImport] = useState(false);
  const [mostraNuovo, setMostraNuovo] = useState(false);

  const [dettaglio, setDettaglio] = useState<ContactDetail | null>(null);
  const [caricandoDettaglio, setCaricandoDettaglio] = useState(false);
  const [esportando, setEsportando] = useState(false);

  const filtri = useMemo(
    () => ({ search: ricercaAttiva, status: stato }),
    [ricercaAttiva, stato]
  );

  // La ricerca parte da sola dopo una pausa: senza, ogni lettera sarebbe una
  // chiamata al database.
  useEffect(() => {
    const t = setTimeout(() => {
      setRicercaAttiva(ricerca);
      setPagina(0);
    }, 350);
    return () => clearTimeout(t);
  }, [ricerca]);

  // Cambiando cosa si guarda, la selezione di prima non vale piu': rischierebbe
  // di spostare contatti che non si vedono.
  useEffect(() => {
    setSelezionati(new Set());
  }, [filtri]);

  const caricaContatti = useCallback(
    async (inSecondoPiano = false) => {
      if (!inSecondoPiano) setLoading(true);
      try {
        const [elenco, riepilogo, tutteLeListe] = await Promise.all([
          emailService.getContacts({ ...filtri, page: pagina }),
          emailService.getStats(),
          listeService.elenco(),
        ]);
        setContatti(elenco.contatti);
        setTotale(elenco.totale);
        setStats(riepilogo);
        setListe(tutteLeListe);
      } catch (errore) {
        addNotification({ type: 'error', title: 'Non riesco a caricare i contatti', message: messaggioErrore(errore) });
      } finally {
        setLoading(false);
      }
    },
    [filtri, pagina, addNotification]
  );

  useEffect(() => {
    caricaContatti();
  }, [caricaContatti]);

  const apriDettaglio = useCallback(
    async (id: string) => {
      setCaricandoDettaglio(true);
      try {
        setDettaglio(await emailService.getContactDetail(id));
      } catch (errore) {
        addNotification({ type: 'error', title: 'Non riesco ad aprire il contatto', message: messaggioErrore(errore) });
      } finally {
        setCaricandoDettaglio(false);
      }
    },
    [addNotification]
  );

  const disiscrivi = useCallback(async () => {
    if (!dettaglio) return;
    try {
      await emailService.disiscrivi(dettaglio.contatto.id, 'richiesta diretta, registrata a mano');
      addNotification({
        type: 'success',
        title: 'Contatto disiscritto',
        message: `${dettaglio.contatto.email} non ricevera' piu' campagne.`,
      });
      await apriDettaglio(dettaglio.contatto.id);
      await caricaContatti(true);
    } catch (errore) {
      addNotification({ type: 'error', title: 'Non riesco a disiscriverlo', message: messaggioErrore(errore) });
    }
  }, [dettaglio, addNotification, apriDettaglio, caricaContatti]);

  const esporta = useCallback(async () => {
    setEsportando(true);
    try {
      const quanti = await emailService.esportaCsv(filtri);
      addNotification({ type: 'success', title: 'Esportazione pronta', message: `${quanti} contatti scaricati.` });
    } catch (errore) {
      addNotification({ type: 'error', title: 'Esportazione non riuscita', message: messaggioErrore(errore) });
    } finally {
      setEsportando(false);
    }
  }, [filtri, addNotification]);

  // --- Selezione ----------------------------------------------------------

  const alternaSelezione = useCallback((id: string) => {
    setSelezionati((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(id)) dopo.delete(id);
      else dopo.add(id);
      return dopo;
    });
  }, []);

  const tuttaLaPaginaSelezionata = contatti.length > 0 && contatti.every((c) => selezionati.has(c.id));

  const alternaPagina = useCallback(() => {
    setSelezionati((prima) => {
      const dopo = new Set(prima);
      if (contatti.every((c) => dopo.has(c.id))) contatti.forEach((c) => dopo.delete(c.id));
      else contatti.forEach((c) => dopo.add(c.id));
      return dopo;
    });
  }, [contatti]);

  const selezionaTutti = useCallback(async () => {
    setLavorandoSelezione(true);
    try {
      setSelezionati(new Set(await emailService.idsCorrispondenti(filtri)));
    } catch (errore) {
      addNotification({ type: 'error', title: 'Non riesco a selezionarli tutti', message: messaggioErrore(errore) });
    } finally {
      setLavorandoSelezione(false);
    }
  }, [filtri, addNotification]);

  const mettiInLista = useCallback(
    async (listaId: string) => {
      const nome = liste.find((l) => l.id === listaId)?.name ?? 'lista';
      const ids = [...selezionati];
      setLavorandoSelezione(true);
      try {
        await listeService.aggiungi(listaId, ids);
        addNotification({
          type: 'success',
          title: 'Messi nella lista',
          message: `${ids.length} ${ids.length === 1 ? 'contatto' : 'contatti'} in "${nome}".`,
        });
        setSelezionati(new Set());
        await caricaContatti(true);
      } catch (errore) {
        addNotification({ type: 'error', title: 'Operazione non riuscita', message: messaggioErrore(errore) });
      } finally {
        setLavorandoSelezione(false);
      }
    },
    [liste, selezionati, caricaContatti, addNotification]
  );

  const nomiListe = useMemo(() => Object.fromEntries(liste.map((l) => [l.id, l.name])), [liste]);

  const kpi = useMemo(
    () => [
      { label: 'Contatti', value: stats.totale, icon: Users, color: 'text-primary-500' },
      { label: 'Possono ricevere', value: stats.attivi, icon: ShieldCheck, color: 'text-green-500' },
      { label: 'In attesa', value: stats.inAttesa, icon: Mail, color: 'text-amber-500' },
      { label: 'Fuori lista', value: stats.fuori, icon: MailX, color: 'text-neutral-500' },
    ],
    [stats]
  );

  const ultimaPagina = Math.max(0, Math.ceil(totale / CONTACTS_PAGE_SIZE) - 1);
  const filtrato = Boolean(ricercaAttiva) || stato !== 'tutti';

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="h-10 w-full max-w-md rounded-lg bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-20 md:h-24 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
        <Card className="h-14 animate-pulse bg-gray-50 border-gray-100" />
        <div className="grid gap-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="h-20 animate-pulse bg-gray-50 border-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <MailingIntestazione descrizione="La rubrica: chi puo' ricevere le campagne. I gruppi di contatti si fanno nella scheda Liste.">
        <Button variant="outline" onClick={esporta} disabled={esportando} className="h-10">
          {esportando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Esporta
        </Button>
        <Button variant="outline" onClick={() => setMostraNuovo(true)} className="h-10">
          <UserPlus className="w-4 h-4 mr-2" />
          Nuovo contatto
        </Button>
        <Button
          onClick={() => setMostraImport(true)}
          className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Upload className="w-4 h-4 mr-2" />
          Importa contatti
        </Button>
      </MailingIntestazione>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpi.map((voce) => (
          <Card key={voce.label} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-[10px] md:text-xs font-medium text-neutral-500 uppercase tracking-wider truncate">
                  {voce.label}
                </p>
                <voce.icon className={cn('h-3 w-3 md:h-4 md:w-4 flex-shrink-0', voce.color)} />
              </div>
              <div className="text-xl md:text-2xl font-bold text-gray-900">{voce.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ricerca e filtri */}
      <Card className="p-2">
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca per indirizzo, nome o azienda…"
              className="pl-9 h-10 border-0 focus-visible:ring-0"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 px-1 pb-1">
            {FILTRI.map((f) => (
              <button
                key={f.valore}
                type="button"
                onClick={() => {
                  setStato(f.valore);
                  setPagina(0);
                }}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  stato === f.valore
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-neutral-600 border-gray-200 hover:border-gray-300'
                )}
              >
                {f.etichetta}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Elenco */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none">
            {contatti.length > 0 && (
              <input
                type="checkbox"
                checked={tuttaLaPaginaSelezionata}
                onChange={alternaPagina}
                className="w-4 h-4 rounded border-gray-300 accent-primary-600"
                aria-label="Seleziona tutti quelli in questa pagina"
              />
            )}
            {totale} {totale === 1 ? 'contatto' : 'contatti'}
          </label>
          {ultimaPagina > 0 && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pagina === 0} onClick={() => setPagina((p) => Math.max(0, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-neutral-500">
                {pagina + 1} / {ultimaPagina + 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= ultimaPagina}
                onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {contatti.length === 0 ? (
          <div className="text-center py-14 rounded-xl border border-dashed border-gray-200 bg-white">
            <AtSign className="w-12 h-12 text-neutral-400 mx-auto mb-4 opacity-30" />
            <p className="text-neutral-500">
              {filtrato ? 'Nessun contatto per questa ricerca' : 'Nessun contatto ancora'}
            </p>
            {!filtrato && (
              <>
                <p className="text-xs text-neutral-400 mt-2 max-w-md mx-auto">
                  I contatti arrivano da soli da lead, clienti e fornitori che hanno un indirizzo
                  email. Puoi anche caricare un file.
                </p>
                <Button className="mt-4" onClick={() => setMostraImport(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Importa il primo file
                </Button>
              </>
            )}
          </div>
        ) : (
          contatti.map((c) => (
            <Card
              key={c.id}
              onClick={() => apriDettaglio(c.id)}
              className={cn(
                'group relative overflow-hidden p-4 cursor-pointer bg-white border-gray-200 hover:border-primary-500 hover:shadow-xl transition-all duration-300',
                selezionati.has(c.id) && 'border-primary-400 bg-primary-50/40'
              )}
            >
              <div
                className={cn(
                  'absolute left-0 top-0 h-full w-1 opacity-70 group-hover:opacity-100 transition-opacity',
                  EMAIL_CONTACT_STATUS[c.status].puntino
                )}
              />
              <div className="flex items-start gap-3 pl-2">
                <input
                  type="checkbox"
                  checked={selezionati.has(c.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => alternaSelezione(c.id)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 accent-primary-600 flex-shrink-0"
                  aria-label={`Seleziona ${c.email}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-sm md:text-base text-gray-900 group-hover:text-primary-600 transition-colors break-all">
                      {c.email}
                    </h3>
                    <ConsentBadge status={c.status} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                    {c.full_name && <span>{c.full_name}</span>}
                    {c.company_name && (
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-neutral-400" />
                        <span className="truncate max-w-[220px]">{c.company_name}</span>
                      </span>
                    )}
                    {c.consent_basis && (
                      <span className="text-neutral-400">{EMAIL_CONSENT_BASIS_LABEL[c.consent_basis]}</span>
                    )}
                  </div>
                  {c.liste.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.liste.map((id) => (
                        <span
                          key={id}
                          className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-50 text-primary-700 border border-primary-100"
                        >
                          {nomiListe[id] ?? 'lista'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}

        <BarraSelezione
          quanti={selezionati.size}
          totale={totale}
          liste={liste}
          lavorando={lavorandoSelezione}
          onSelezionaTutti={selezionaTutti}
          onAggiungi={mettiInLista}
          onAnnulla={() => setSelezionati(new Set())}
        />
      </div>

      <ImportContactsModal
        open={mostraImport}
        onClose={() => setMostraImport(false)}
        onImported={() => caricaContatti(true)}
        liste={liste}
      />

      <NuovoContattoDialog
        aperta={mostraNuovo}
        liste={liste}
        listaProposta={null}
        onChiudi={() => setMostraNuovo(false)}
        onAggiunto={() => caricaContatti(true)}
      />

      <SchedaContatto
        dettaglio={dettaglio}
        caricando={caricandoDettaglio}
        liste={liste}
        onChiudi={() => setDettaglio(null)}
        onDisiscrivi={disiscrivi}
        onListeCambiate={() => caricaContatti(true)}
      />
    </div>
  );
}
